import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL, PRIORITY_LABEL, STATUS_TONE, TERMINAL_STATUSES, groupStatusCounts, groupClientStatusCounts, isClientStatusRole, getClientDisplayStatus, getTicketDisplayStatus } from "@/lib/constants";
import StatusBadge from "@/components/common/StatusBadge";
import PriorityBadge from "@/components/common/PriorityBadge";
import SLATimer from "@/components/common/SLATimer";
import HBarChart from "@/components/charts/HBarChart";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/lib/date";
import {
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { AlertCircle, ArrowRight, CheckCircle2, FileEdit, Flame, Layers, Users } from "lucide-react";

const GOLD = "#2563eb";

const STATUS_TONE_COLORS = {
  blue: "#3b82f6", cyan: "#0891b2", gold: "#8b5cf6", amber: "#f59e0b", orange: "#f97316",
  green: "#16a34a", red: "#ef4444", slate: "#64748b", purple: "#8b5cf6",
};

const PRIORITY_COLORS = {
  P1: "#dc2626",
  P2: "#f97316",
  P3: "#f59e0b",
  P4: "#2563eb",
  P5: "#94a3b8",
};

function statusChartColor(status, index = 0) {
  const fallback = [
    "#2563eb",
    "#3b82f6",
    "#8b5cf6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#06b6d4",
    "#ec4899",
  ];

  return STATUS_TONE_COLORS[STATUS_TONE[status]] || fallback[index % fallback.length];
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    let active = true;
    let latestRequest = 0;
    const load = () => {
      const requestId = ++latestRequest;
      api.get("/dashboard/summary", { params: { _ts: Date.now() } })
        .then((response) => {
          if (active && requestId === latestRequest) setData(response.data);
        });
    };

    load();
    const refreshInterval = window.setInterval(load, 30_000);
    window.addEventListener("focus", load);
    window.addEventListener("helpdesk-ticket-changed", load);
    return () => {
      active = false;
      window.clearInterval(refreshInterval);
      window.removeEventListener("focus", load);
      window.removeEventListener("helpdesk-ticket-changed", load);
    };
  }, []);
  if (!data) return <DashboardSkeleton />;

  const clientRole = isClientStatusRole(data.role);
  const completedStatusFilter = clientRole
    ? [...TERMINAL_STATUSES].sort().join(",")
    : "completed";
  const statusData = (clientRole
    ? groupClientStatusCounts(data.by_status)
    : groupStatusCounts(data.by_status)).map((bar, index) => ({
    ...bar,
    color: statusChartColor(bar.colorStatus || bar.statuses[0], index),
  }));
  const priorityData = (data.by_priority || [])
    .map((s) => ({
      name: s._id || "P?",
      value: s.count,
      color: PRIORITY_COLORS[s._id] || "#94a3b8",
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const priorityTotal = priorityData.reduce((sum, p) => sum + (p.value || 0), 0);

  const defaultKpis = [
    { label: "Total tickets", value: data.total, icon: Layers, tone: "text-foreground", testid: "kpi-total", href: "/tickets" },
    { label: clientRole ? "Active tickets" : "Open", value: data.open, icon: FileEdit, tone: "text-sky-700", testid: "kpi-open", href: "/tickets?open=true" },
    { label: "Critical (P1)", value: data.critical, icon: Flame, tone: "text-rose-700", testid: "kpi-critical", href: "/tickets?priority=P1&open=true" },
    { label: "SLA at risk", value: data.sla_at_risk, icon: AlertCircle, tone: "text-amber-700", testid: "kpi-sla", href: "/tickets?sla=at_risk" },
    { label: "Completed", value: data.closed, icon: CheckCircle2, tone: "text-emerald-700", testid: "kpi-closed", href: `/tickets?status=${completedStatusFilter}` },
  ];

  const organizationAdminKpis = [
    { label: "Client users", value: data.organization_users ?? 0, icon: Users, tone: "text-violet-700", testid: "kpi-org-users", href: "/admin/users" },
    { label: "Active tickets", value: data.open, icon: FileEdit, tone: "text-sky-700", testid: "kpi-open", href: "/tickets?open=true" },
    {
      label: "Client confirmation pending",
      value:
        (data.by_status || []).find(
          (item) => item._id === "client_confirmation_pending"
        )?.count ?? 0,
      icon: AlertCircle,
      tone: "text-orange-700",
      testid: "kpi-waiting-client",
      href: "/tickets?status=client_confirmation_pending",
    },
    { label: "Completed tickets", value: data.closed, icon: CheckCircle2, tone: "text-emerald-700", testid: "kpi-closed", href: `/tickets?status=${completedStatusFilter}` },
    { label: "SLA at risk", value: data.sla_at_risk, icon: Flame, tone: "text-rose-700", testid: "kpi-sla", href: "/tickets?sla=at_risk" },
  ];

  const kpis = data.role === "client_admin"
    ? organizationAdminKpis
    : defaultKpis;

  if (["support_admin", "super_admin"].includes(data.role)) {
    kpis.push({
      label: "Unassigned",
      value: data.unassigned,
      icon: Users,
      tone: "text-orange-700",
      testid: "kpi-unassigned",
      href: "/tickets?unassigned=true",
    });
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            {ROLE_LABEL[data.role]} · overview
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
            Welcome back, <span className="gold-text">{user?.name || "User" || "there"}</span>.
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <Link key={k.label} to={k.href || "/tickets"} data-testid={k.testid}
              className="rounded-xl border border-border bg-card p-4 hover:border-primary/40 hover:bg-card transition-colors cursor-pointer group">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground group-hover:text-foreground">{k.label}</div>
                <Icon className={`h-4 w-4 ${k.tone}`} />
              </div>
              <div className={`mt-2 font-display text-3xl font-bold ${k.tone}`}>
                {k.value}
              </div>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Tickets by status" className="lg:col-span-2">
          <HBarChart
            data={statusData}
            yLabel="Status"
            tooltip={<DashboardTooltip />}
            onBarClick={(d) => d?.status && navigate(`/tickets?status=${d.status}`)}
          />
        </ChartCard>
        <ChartCard title="By priority">
          <div className="relative">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={priorityData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={62}
                  outerRadius={92}
                  paddingAngle={2}
                  stroke="#ffffff"
                  strokeWidth={2}
                  startAngle={90}
                  endAngle={-270}
                  onClick={(d, index) => {
                    const code = priorityData[index]?.name ?? d?.payload?.name;
                    if (code) navigate(`/tickets?priority=${code}`);
                  }}
                  cursor="pointer"
                >
                  {priorityData.map((p) => (
                    <Cell key={p.name} fill={p.color} />
                  ))}
                </Pie>
                <Tooltip content={<DashboardTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <div className="font-mono text-2xl font-semibold text-foreground">{priorityTotal}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Tickets</div>
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            {priorityData.map((p) => (
              <button
                type="button"
                key={p.name}
                onClick={() => navigate(`/tickets?priority=${p.name}`)}
                className="flex w-full items-center justify-between gap-3 rounded-md px-1.5 py-1 text-xs hover:bg-muted/60"
              >
                <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: p.color }} />
                  <span className="truncate">{PRIORITY_LABEL[p.name] || p.name}</span>
                </span>
                <span className="font-mono font-semibold text-foreground">
                  {p.value}
                  <span className="ml-2 text-muted-foreground font-normal">
                    {priorityTotal ? Math.round((p.value / priorityTotal) * 100) : 0}%
                  </span>
                </span>
              </button>
            ))}
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Recent tickets</div>
            <Link
              to="/tickets"
              data-testid="recent-view-all"
              className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:border-primary/40 hover:bg-accent"
            >
              View all
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {data.recent.length === 0 ? (
            <div className="text-xs text-muted-foreground py-4">No tickets yet — click <b>New Ticket</b> to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-3 py-2 text-left">Ticket</th>
                    <th className="px-3 py-2 text-left">Title</th>
                    <th className="px-3 py-2 text-left">Priority</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">SLA</th>
                    <th className="px-3 py-2 text-right">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.slice(0, 10).map((t) => (
                    <tr
                      key={t.id}
                      data-row
                      data-testid={`recent-ticket-${t.ticket_number}`}
                      onClick={() => navigate(`/tickets/${t.id}`)}
                      className="cursor-pointer border-b border-border last:border-b-0"
                    >
                      <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-muted-foreground">
                        <Link to={`/tickets/${t.id}`} className="hover:text-primary" onClick={(e) => e.stopPropagation()}>
                          {t.ticket_number}
                        </Link>
                      </td>
                      <td className="max-w-[360px] truncate px-3 py-2.5 font-medium" title={t.title}>{t.title}</td>
                      <td className="px-3 py-2.5"><PriorityBadge priority={t.priority} /></td>
                      <td className="px-3 py-2.5"><StatusBadge status={clientRole ? getClientDisplayStatus(t) : getTicketDisplayStatus(t)} /></td>
                      <td className="px-3 py-2.5">
                        {t.resolution_due ? (
                          <SLATimer
                            deadline={t.resolution_due}
                            status={clientRole ? getClientDisplayStatus(t) : getTicketDisplayStatus(t)}
                            completedAt={t.closed_at || t.resolved_at}
                            compact
                          />
                        ) : (clientRole ? getClientDisplayStatus(t) : getTicketDisplayStatus(t)) === "invalid" ? (
                          <span className="text-xs text-muted-foreground">-</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right text-xs text-muted-foreground">
                        {t.created_at ? formatDate(t.created_at) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DashboardTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const item = payload[0];
  const source = item?.payload || {};

  return (
    <div className="md-chart-tooltip min-w-[150px] rounded-lg border px-3 py-2 shadow-xl">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label || source.name || "Details"}
      </div>

      <div className="mt-1 flex items-center justify-between gap-5">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{
              backgroundColor:
                source.color ||
                item.color ||
                GOLD,
            }}
          />
          <span className="truncate text-sm font-medium">
            {source.name || item.name || "Tickets"}
          </span>
        </div>

        <span className="font-mono text-sm font-bold">
          {item.value ?? source.value ?? 0}
        </span>
      </div>

    </div>
  );
}


function DashboardSkeleton() {
  return (
    <div className="space-y-6" data-testid="dashboard-skeleton">
      <div className="space-y-3">
        <Skeleton className="h-3 w-36" />
        <Skeleton className="h-10 w-full max-w-xl" />
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="rounded-xl border border-border bg-card p-4"
          >
            <div className="flex items-center justify-between">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-4 rounded-full" />
            </div>
            <Skeleton className="mt-4 h-9 w-16" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4 lg:col-span-2">
          <Skeleton className="mb-5 h-3 w-32" />
          <Skeleton className="h-[200px] w-full" />
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <Skeleton className="mb-5 h-3 w-24" />
          <Skeleton className="mx-auto h-[180px] w-[180px] rounded-full" />
        </div>
      </div>
    </div>
  );
}


function ChartCard({ title, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-border bg-card p-4 ${className}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">{title}</div>
      {children}
    </div>
  );
}
